import { useEffect, useMemo, useRef, useState } from "react";
import { MapPin, Loader2, Navigation } from "lucide-react";
import { Input } from "./ui/input";
import { Popover, PopoverAnchor, PopoverContent } from "./ui/popover";
import { api } from "../lib/api";
import { useLang } from "../context/LanguageContext";

/**
 * Gateadressefelt med forslag frå Kartverket (Geonorge) medan ein skriv.
 * Vel eit forslag → onSelect({adressetekst, postnummer, poststed, kommune, lat, lon}) slik at forelderen
 * kan fylle postnummer og poststed òg. Fritekst er framleis lov (onChange).
 *
 * Props: id, value, onChange(text), onSelect(item), postalCode (snevrar inn søket), placeholder, testId, limit
 */
export const AddressInput = ({ id, value = "", onChange, onSelect, postalCode = "", placeholder, className = "", limit = 8, disabled = false, required = false, testId = "address-input" }) => {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const selectedRef = useRef(""); // teksten vi sist valde – ikkje søk på nytt før brukaren endrar han
  const inputRef = useRef(null);
  const listId = useMemo(() => `${id || "address"}-listbox`, [id]);

  useEffect(() => {
    const q = (value || "").trim();
    if (!open || q.length < 2 || q === selectedRef.current) {
      if (q.length < 2) setItems([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/adresser", { params: { q, limit, postnummer: /^\d{4}$/.test(postalCode || "") ? postalCode : undefined } });
        if (!cancelled) {
          setItems(data.items || []);
          setActive(0);
          setUnavailable(false);
        }
      } catch (err) {
        if (!cancelled) {
          setItems([]);
          setUnavailable(err?.response?.status === 424);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 200);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [value, open, limit, postalCode]);

  const select = (item) => {
    selectedRef.current = item.adressetekst;
    setItems([]);
    setOpen(false);
    onChange?.(item.adressetekst);
    onSelect?.(item);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const handleKeyDown = (e) => {
    if (!open || items.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => (a + 1) % items.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => (a - 1 + items.length) % items.length); }
    else if (e.key === "Enter") { e.preventDefault(); select(items[active]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  const showList = open && items.length > 0;

  return (
    <div className={className}>
      <Popover open={showList} onOpenChange={(o) => { if (!o) setOpen(false); }}>
        <PopoverAnchor asChild>
          <div className="relative">
            <Input
              ref={inputRef}
              id={id}
              value={value}
              onChange={(e) => { selectedRef.current = ""; setOpen(true); onChange?.(e.target.value); }}
              onFocus={() => setOpen(true)}
              onBlur={() => setTimeout(() => setOpen(false), 120)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder || t("F.eks. Skålagato 1")}
              autoComplete="off"
              disabled={disabled}
              required={required}
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={showList}
              aria-controls={listId}
              aria-activedescendant={showList ? `${listId}-opt-${active}` : undefined}
              className="pr-8"
              data-testid={testId}
            />
            <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Navigation className="w-4 h-4" />}
            </span>
          </div>
        </PopoverAnchor>
        <PopoverContent
          align="start"
          sideOffset={4}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          className="p-1 w-max min-w-[var(--radix-popover-trigger-width)] max-w-[min(28rem,calc(100vw-2rem))] max-h-72 overflow-y-auto bg-popover/95 backdrop-blur-md"
          data-testid={`${testId}-suggestions`}
        >
          <ul id={listId} role="listbox" aria-label={t("Adresseforslag")}>
            {items.map((it, i) => (
              <li
                key={`${it.adressetekst}-${it.postnummer}`}
                id={`${listId}-opt-${i}`}
                role="option"
                aria-selected={i === active}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setActive(i)}
                onClick={() => select(it)}
                className={`flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm cursor-pointer transition-colors duration-150 ${i === active ? "bg-secondary" : "hover:bg-secondary/70"}`}
                data-testid={`${testId}-option-${i}`}
              >
                <span className="flex items-center gap-2 min-w-0">
                  <MapPin className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
                  <span className="truncate font-medium">{it.adressetekst}</span>
                </span>
                <span className="text-xs text-muted-foreground shrink-0">{it.postnummer} {it.poststed}</span>
              </li>
            ))}
          </ul>
          <p className="px-3 pt-1 pb-1.5 text-[10px] text-muted-foreground">{t("Kilde: Kartverket")}</p>
        </PopoverContent>
      </Popover>
      {unavailable && <p className="mt-1 text-xs text-muted-foreground" data-testid={`${testId}-unavailable`}>{t("Adresseforslag er utilgjengelig akkurat nå – skriv adressen manuelt.")}</p>}
    </div>
  );
};
